import type { RequestMiddleware } from "graphql-request";

import { OPERATION_KIND, resolveBodyOperationKind } from "~/helpers/offline/graphqlOperations";
import encodeQueueMeta, {
	CLIENT_ID_HEADER,
	OPERATION_KIND_HEADER,
	QUEUE_META_HEADER,
	READ_CACHE_KEY_HEADER
} from "~/helpers/offline/queueMeta";
import resolveQueueMeta from "~/helpers/offline/queueMetaRegistry";
import { buildCurrentScopeReadCacheKey } from "~/helpers/offline/readCacheScope";
import isCacheableReadOperation, {
	isHistoryCacheOperation,
	isLocationTreeCacheOperation,
	isMaterialCatalogueCacheOperation,
	isQuantityCacheOperation,
	isReferenceCacheOperation
} from "~/helpers/sw/readCacheAllowlist";
import wsId from "~/helpers/wsId";

const isReadCacheEligibleOperation = (operationName: string | undefined): boolean =>
	isCacheableReadOperation(operationName) ||
	isHistoryCacheOperation(operationName) ||
	isReferenceCacheOperation(operationName) ||
	isQuantityCacheOperation(operationName) ||
	isLocationTreeCacheOperation(operationName) ||
	isMaterialCatalogueCacheOperation(operationName);

const stampMutationHeaders = (headers: Headers, operationName: string | undefined, variables: unknown): void => {
	if (!headers.get(CLIENT_ID_HEADER)) headers.set(CLIENT_ID_HEADER, wsId());
	if (!operationName || headers.get(QUEUE_META_HEADER)) return;
	const meta = resolveQueueMeta(operationName, variables, Date.now());
	if (meta) headers.set(QUEUE_META_HEADER, encodeQueueMeta(meta));
};

const stampReadCacheKey = (headers: Headers, operationName: string | undefined, variables: unknown): void => {
	if (!isReadCacheEligibleOperation(operationName)) return;
	const key = buildCurrentScopeReadCacheKey({ operationName, variables });
	if (key) headers.set(READ_CACHE_KEY_HEADER, key);
};

const offlineRequestMiddleware: RequestMiddleware = (request) => {
	const body = typeof request.body === "string" ? request.body : undefined;
	const kind = resolveBodyOperationKind(body);
	if (kind === undefined) return request;

	const headers = new Headers(request.headers);
	headers.set(OPERATION_KIND_HEADER, kind);

	if (kind === OPERATION_KIND.MUTATION) stampMutationHeaders(headers, request.operationName, request.variables);
	else if (kind === OPERATION_KIND.QUERY) stampReadCacheKey(headers, request.operationName, request.variables);

	return { ...request, headers };
};

export default offlineRequestMiddleware;
